import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type CancelShiftPolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ProgressCarousel } from "@src/appV2/redesign/components/ProgressCarousel";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";

import { EducationStepNavigation } from "./EducationStepNavigation";
import { LifeHappensExamplesCard } from "./LifeHappensExamplesCard";

interface LifeHappensStepProps {
  cancellationPolicy: CancelShiftPolicy[];
  onNext: () => void;
  onBack: () => void;
  progressCarousel?: {
    currentIndex: number;
    totalSteps: number;
    onIndexChange: (index: number) => void;
  };
}

export function LifeHappensStep(props: LifeHappensStepProps) {
  const { onNext, onBack, cancellationPolicy, progressCarousel } = props;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <TopLeftBackButton iconType="chevron-left" onClick={onBack} />
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "28%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            paddingX: 2,
          }}
        >
          <LifeHappensExamplesCard cancellationPolicy={cancellationPolicy} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "65%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Stack spacing={6} alignItems="center" sx={{ width: "100%", maxWidth: 343 }}>
            <Text
              variant="h4"
              sx={(theme) => ({
                fontSize: "32px",
                color: theme.palette.text.primary,
                lineHeight: "35px",
                textAlign: "center",
              })}
            >
              Life happens! If plans change, cancel early
            </Text>
            <Stack spacing={2} sx={{ maxWidth: 343 }}>
              <Text
                variant="body1"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: "16px",
                  lineHeight: "17px",
                  textAlign: "center",
                })}
              >
                Cancelling at least a day in advance protects your score and gives workplaces time
                to find a replacement.
              </Text>
              <Text
                variant="body1"
                sx={(theme) => ({
                  color: theme.palette.error.main,
                  fontSize: "16px",
                  lineHeight: "17px",
                  textAlign: "center",
                })}
              >
                A score of zero leads to a Restriction, which removes your future shifts and
                prevents you from booking new shifts for 7 days.
              </Text>
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%" }}>
            {progressCarousel && (
              <Box sx={{ paddingX: 2 }}>
                <ProgressCarousel
                  currentIndex={progressCarousel.currentIndex}
                  totalCount={progressCarousel.totalSteps}
                  onIndexChange={progressCarousel.onIndexChange}
                />
              </Box>
            )}
            <EducationStepNavigation onNext={onNext} onBack={onBack} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
